import { Divider } from "@octopusdeploy/design-system-components";
import type { ActionTemplateCategoryResource, ActionTemplateSearchResource } from "@octopusdeploy/octopus-server-client";
import { ActionHandlerCategory } from "@octopusdeploy/octopus-server-client";
import type { LinkHref } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { Environment } from "@octopusdeploy/utilities";
import * as React from "react";
import CategoryInfo from "~/areas/projects/components/Process/ActionTemplateSelector/CategoryInfo";
import CategorySelector from "~/areas/projects/components/Process/ActionTemplateSelector/CategorySelector";
import { InstalledActionTemplateList } from "~/areas/projects/components/Process/ActionTemplateSelector/InstalledActionTemplateList";
import type { WithProcessContextInjectedProps } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import { withProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import type { WithProcessQueryStringContextInjectedProps } from "~/areas/projects/components/Process/Contexts/ProcessQueryString/ProcessQueryStringContext";
import { withProcessQueryStringContext } from "~/areas/projects/components/Process/Contexts/ProcessQueryString/ProcessQueryStringContext";
import { ProcessSubPageLayout } from "~/areas/projects/components/Process/ProcessSubPageLayout";
import { isBlueprintProcessIdentifier } from "~/areas/projects/components/Process/types";
import { repository } from "~/clientInstance";
import { CommunityActionTemplateList } from "~/components/ActionTemplates/CommunityActionTemplateList";
import filterActionTemplates from "~/components/ActionTemplates/filterActionTemplates";
import pluginRegistry from "~/components/Actions/pluginRegistry";
import { createSampleStepActionTemplateResource } from "~/components/Actions/sample/SampleStepPackage";
import type { DataBaseComponentState } from "~/components/DataBaseComponent/DataBaseComponent";
import { DataBaseComponent } from "~/components/DataBaseComponent/DataBaseComponent";
import FilterSearchBox from "~/components/FilterSearchBox/FilterSearchBox";
import Section from "~/components/Section";
import styles from "./style.module.less";
interface ActionTemplateSelectorForBlueprintState extends DataBaseComponentState {
    isLoaded: boolean;
    templates: ActionTemplateSearchResource[];
    categories: ActionTemplateCategoryResource[];
    childActionTypes: string[];
    filterText?: string;
    showCommunityTemplates: boolean;
    selectedCategory?: ActionHandlerCategory;
    communityStepCategories: ActionHandlerCategory[];
}
export type ActionTemplateSelectorForBlueprintProps = {
    runbookId?: string;
    parentStepId?: string;
    errors: Error[];
    busy: boolean;
} & WithProcessContextInjectedProps & WithProcessQueryStringContextInjectedProps;
class ActionTemplateSelectorForBlueprint extends DataBaseComponent<ActionTemplateSelectorForBlueprintProps, ActionTemplateSelectorForBlueprintState> {
    constructor(props: ActionTemplateSelectorForBlueprintProps) {
        super(props);
        this.state = {
            isLoaded: false,
            templates: [],
            categories: [],
            childActionTypes: [],
            showCommunityTemplates: false,
            selectedCategory: this.getDefaultCategory(),
            communityStepCategories: [
                ActionHandlerCategory.Aws,
                ActionHandlerCategory.Azure,
                ActionHandlerCategory.Certificate,
                ActionHandlerCategory.Docker,
                ActionHandlerCategory.GoogleCloud,
                ActionHandlerCategory.JavaAppServer,
                ActionHandlerCategory.Kubernetes,
                ActionHandlerCategory.Package,
                ActionHandlerCategory.Script,
                ActionHandlerCategory.Terraform,
                ActionHandlerCategory.WindowsServer,
            ],
        };
    }
    async componentDidMount() {
        await this.doBusyTask(async () => {
            const actionsPlugins = pluginRegistry.getAllActions();
            const categories = await repository.ActionTemplates.categories();
            let templates = await fetchTemplates();
            let updatedCommunityStepCategories = this.state.communityStepCategories;
            const childActionTypes = actionsPlugins.filter((action) => action.canBeChild).map((action) => action.actionType);
            if (this.state.selectedCategory) {
                templates = this.getTemplatesWithUpdatedCategories(templates, this.state.selectedCategory);
                updatedCommunityStepCategories = this.state.communityStepCategories.filter((x) => x != this.state.selectedCategory);
            }
            this.setState({
                isLoaded: true,
                categories: categories,
                templates: templates,
                childActionTypes,
                communityStepCategories: updatedCommunityStepCategories,
            });
        });
    }
    render() {
        const communityTemplates = this.state.templates.filter((at) => !at.IsInstalled);
        return (<ProcessSubPageLayout title={<div className={styles.pageTitle}>Add Step Template</div>} busy={this.state.busy} errors={this.errors}>
                {this.state.isLoaded && (<div className={styles.container}>
                        <Section>
                            <FilterSearchBox placeholder="Filter by name, category or description..." value={this.state.filterText} onChange={this.filterChanged} fullWidth={true} containerClassName={styles.filterSearchBoxContainer} autoFocus={true} debounceDelay={500}/>
                        </Section>
                        <Divider />
                        <div className={styles.stepSelectionContainer}>
                            <div className={styles.categoriesContainer}>
                                <CategorySelector categories={this.state.categories} selectedCategory={this.state.selectedCategory} steps={this.state.templates} onCategorySelected={this.categoryChanged}/>
                            </div>
                            <Divider />
                            <div className={styles.stepsContainer}>
                                {this.state.selectedCategory ? <CategoryInfo categories={this.state.categories} selectedCategory={this.state.selectedCategory}/> : <></>}
                                {this.state.filterText ? <div className={styles.searchHeader}>Results for "{this.state.filterText}"</div> : <></>}
                                <InstalledActionTemplateList categories={this.state.categories} templates={this.installedTemplates()} communityTemplates={communityTemplates} onDetailsUrlRequested={this.getDetailsUrl} onPostSelectionUrlRequested={this.getNewStepUrl} filter={this.state.filterText?.toLowerCase()} activeCategory={this.state.selectedCategory}/>
                                {this.state.selectedCategory !== ActionHandlerCategory.Featured && this.shouldShowCommunityTemplateList(this.state.selectedCategory) && (<CommunityActionTemplateList spaceId={this.props.processContext.state.processIdentifier.spaceId} templates={communityTemplates} filter={this.state.filterText?.toLowerCase()} installationActionName="Install and Add Step" onDetailsUrlRequested={this.getDetailsUrl} onPostSelectionUrlRequested={this.getNewStepUrl} activeCategory={this.state.selectedCategory !== ActionHandlerCategory.Community ? this.state.selectedCategory : undefined}/>)}
                            </div>
                        </div>
                    </div>)}
            </ProcessSubPageLayout>);
    }
    private shouldShowCommunityTemplateList(category: ActionHandlerCategory | undefined): boolean {
        if (this.state.filterText) {
            return true;
        }
        return !!category && category !== ActionHandlerCategory.BuiltInStep && category !== ActionHandlerCategory.StepTemplate && category !== ActionHandlerCategory.Other;
    }
    private installedTemplates(): ActionTemplateSearchResource[] {
        const installed = this.state.templates.filter((at) => at.IsInstalled);
        const parentStepId = this.props.parentStepId;
        if (!parentStepId) {
            return installed;
        }
        return installed.filter((at) => this.state.childActionTypes.includes(at.Type));
    }
    private getDetailsUrl = (template: ActionTemplateSearchResource): LinkHref => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return links.communityStepTemplatePage.generateUrl({ spaceId: this.props.processContext.state.processIdentifier.spaceId, templateId: template.CommunityActionTemplateId! });
    };
    private getNewStepUrl = (template: {
        Type: string;
        Id: string | null;
    }): LinkHref => {
        const processIdentifier = this.props.processContext.state.processIdentifier;
        if (!isBlueprintProcessIdentifier(processIdentifier)) {
            throw new Error("Only blueprint processes are supported");
        }
        return links.editBlueprintPage.generateUrl({ blueprintId: processIdentifier.blueprintId, spaceId: processIdentifier.spaceId }, { new: true, actionType: template.Type, parentStepId: this.props.parentStepId, templateId: template.Id ?? undefined });
    };
    private getDefaultCategory = (): ActionHandlerCategory => {
        // Process
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const actionCategory = ActionHandlerCategory[this.props.processQueryStringContext.state.queryFilter.actionCategory as keyof typeof ActionHandlerCategory];
        return actionCategory ? actionCategory : ActionHandlerCategory.Featured;
    };
    private categoryChanged = (newCategory: ActionHandlerCategory | undefined) => {
        if (newCategory && this.state.communityStepCategories.some((x) => x === newCategory)) {
            const updatedTemplates = this.getTemplatesWithUpdatedCategories(this.state.templates, newCategory);
            const updatedCategoriesToFindInCommunitySteps = this.state.communityStepCategories.filter((x) => x != newCategory);
            this.setState({ filterText: undefined, selectedCategory: newCategory, templates: updatedTemplates, communityStepCategories: updatedCategoriesToFindInCommunitySteps });
        }
        else {
            this.setState({ filterText: undefined, selectedCategory: newCategory });
        }
    };
    private filterChanged = (newFilterText: string) => {
        const filterStr = newFilterText?.trim().replace(/  +/g, " ");
        this.setState({ filterText: filterStr ? filterStr : undefined, selectedCategory: filterStr ? undefined : this.getDefaultCategory() });
    };
    private getTemplatesWithUpdatedCategories(templates: ActionTemplateSearchResource[], newCategory: ActionHandlerCategory) {
        const communitySteps = templates.filter((x) => x.Categories.includes(ActionHandlerCategory.StepTemplate) && !x.Categories.includes(newCategory));
        const communityStepsToUpdateCategories = filterActionTemplates(communitySteps, newCategory.toLocaleLowerCase()).map((x) => x.Id);
        const updatedTemplates = templates.map((x) => (!communityStepsToUpdateCategories.includes(x.Id) ? x : { ...x, Categories: [...x.Categories, newCategory] }));
        return updatedTemplates;
    }
    static displayName = "ActionTemplateSelectorForBlueprint";
}
async function fetchTemplates(): Promise<ActionTemplateSearchResource[]> {
    const templates = await repository.ActionTemplates.search();
    if (Environment.isInDevelopmentMode()) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const inDevelopmentTemplates = [createSampleStepActionTemplateResource(repository.spaceId!)];
        const templateIds = new Set(templates.map((t) => t.Type));
        const inDevelopmentTemplatesThatArentReturnedByServer = inDevelopmentTemplates.filter((t) => !templateIds.has(t.Type));
        return [...templates, ...inDevelopmentTemplatesThatArentReturnedByServer];
    }
    return templates;
}
const EnhancedActionTemplateSelectorForBlueprint = withProcessContext(withProcessQueryStringContext(ActionTemplateSelectorForBlueprint));
export default EnhancedActionTemplateSelectorForBlueprint;
